/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateField } from '@/shared/ui/components/form/inputs';
import { EnumSelectField } from './EnumSelectField';
import { FieldValueAutocomplete } from './FieldValueAutocomplete';
import { YES_NO_OPTIONS } from './schema';
import type { PropertyFormValues } from './schema';

interface OwnershipAttributeFieldsProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
}

type FlagKey = 'exclusive' | 'publish_authorized';
type UntilKey = 'exclusive_until' | 'publish_authorized_until';

/**
 * A yes/no answer with the date it holds until, shown once the answer is yes.
 *
 * @param props - Form, the flag, its date field and their labels.
 * @returns The pair of fields.
 */
function FlagWithDeadline({ form, flag, until, label, untilLabel }: {
  form: UseFormReturn<PropertyFormValues>;
  flag: FlagKey;
  until: UntilKey;
  label: string;
  untilLabel: string;
}) {
  const answer = form.watch(flag);
  return (
    <div className="grid grid-cols-2 gap-(--space-3)">
      <EnumSelectField form={form} name={flag} label={label} options={YES_NO_OPTIONS} labelPrefix="form.options.yesNo" />
      {answer === 'yes' && (
        <Controller
          name={until}
          control={form.control}
          render={({ field }) => (
            <DateField label={untilLabel} value={field.value ?? ''} onChange={field.onChange} />
          )}
        />
      )}
    </div>
  );
}

/**
 * Exclusivity and publishing authorization (each with its deadline), where
 * the keys are, and the partner. These sit beside the mandate, which keeps
 * its own exclusive type and dates: the two coexist (plan §12).
 *
 * @param props - Form and agency.
 * @returns The ownership attribute fields.
 */
export function OwnershipAttributeFields({ form, agencyId }: OwnershipAttributeFieldsProps) {
  const { t } = useTranslation('features/properties');
  return (
    <div className="flex flex-col gap-(--space-3)" data-testid="ownership-attribute-fields">
      <FlagWithDeadline
        form={form}
        flag="exclusive"
        until="exclusive_until"
        label={t('propertyDrawer.ownership.exclusive')}
        untilLabel={t('propertyDrawer.ownership.exclusiveUntil')}
      />
      <FlagWithDeadline
        form={form}
        flag="publish_authorized"
        until="publish_authorized_until"
        label={t('propertyDrawer.ownership.publishAuthorized')}
        untilLabel={t('propertyDrawer.ownership.publishAuthorizedUntil')}
      />
      <Controller
        name="key_location"
        control={form.control}
        render={({ field }) => (
          <FieldValueAutocomplete
            agencyId={agencyId}
            field="key_location"
            label={t('propertyDrawer.ownership.keyLocation')}
            value={field.value}
            onChange={field.onChange}
            maxLength={120}
          />
        )}
      />
      <Controller
        name="partner_name"
        control={form.control}
        render={({ field }) => (
          <FieldValueAutocomplete
            agencyId={agencyId}
            field="partner_name"
            label={t('propertyDrawer.ownership.partner')}
            value={field.value}
            onChange={field.onChange}
            maxLength={120}
          />
        )}
      />
    </div>
  );
}
